import { cn } from '@ludus/ui'
import { useEffect, useTransition } from 'react'
import { mockManagers } from '@/shared/mocks/managers.ts'
import { useManagerSchedule } from '../data/useScheduleSource.ts'
import type { ManagerId, Weekday } from '../types.ts'
import { toISODate, weekdayFromDate } from '../utils/weekRange.ts'
import { Sidebar } from './Sidebar/Sidebar.tsx'
import { WeekToolbar } from './Toolbar/WeekToolbar.tsx'
import { WeekGrid } from './WeekGrid/WeekGrid.tsx'
import { WindowEditorDialog } from './WindowEditor/WindowEditorDialog.tsx'
import { useEditViewState } from './useEditViewState.ts'

/**
 * Composition root for the Edit tab.
 *
 * State seams:
 *  - `useEditViewState` — purely client/UI state (selected manager, visible week, editor prefill).
 *    Stays in zustand forever; never moves to SWR.
 *  - `useManagerSchedule` — derived "server" state. Today: backed by mock store. Tomorrow:
 *    swap to `useSWR` inside `data/useScheduleSource.ts` — single point of change.
 *
 * `useTransition` wraps coarse navigation setters (manager / week). With the current mock the
 * transition completes synchronously; once schedules come from the network and the grid has
 * many blocks, this keeps the picker/toolbar responsive while the grid re-renders. Use the
 * `isPending` flag to surface a loading hint (currently a soft opacity dim on the main card).
 */
export function EditView() {
  const selectedManagerId = useEditViewState((s) => s.selectedManagerId)
  const setSelectedManager = useEditViewState((s) => s.setSelectedManager)
  const visibleDate = useEditViewState((s) => s.visibleDate)
  const setVisibleDate = useEditViewState((s) => s.setVisibleDate)
  const openEditor = useEditViewState((s) => s.openEditor)

  const [isPending, startTransition] = useTransition()

  // Default-select the first mock manager once on mount.
  // TODO(api): when the real Managers feature ships, replace `mockManagers` with the
  // result of a `useSWR('/managers', fetcher)` call (see comment in `shared/mocks/managers.ts`).
  useEffect(() => {
    if (!selectedManagerId && mockManagers.length > 0) {
      setSelectedManager(mockManagers[0].id)
    }
  }, [selectedManagerId, setSelectedManager])

  const manager = mockManagers.find((m) => m.id === selectedManagerId) ?? null
  const schedule = useManagerSchedule(selectedManagerId)

  const handleManagerSelect = (id: ManagerId) => {
    startTransition(() => setSelectedManager(id))
  }

  const handleDateSelect = (d: Date) => {
    startTransition(() => setVisibleDate(d))
  }

  function handleCreate() {
    if (!manager) return
    const dayDate = visibleDate
    const weekday = weekdayFromDate(dayDate) as Weekday
    openEditor({
      managerId: manager.id,
      date: toISODate(dayDate),
      weekday,
      startMinute: 9 * 60,
      endMinute: 17 * 60,
      workingHours: true,
      scope: 'date',
    })
  }

  return (
    <div className="@container/cal flex h-full w-full flex-1 gap-3 overflow-hidden">
      <Sidebar
        visibleDate={visibleDate}
        selectedManagerId={selectedManagerId}
        onDateSelect={handleDateSelect}
        onManagerSelect={handleManagerSelect}
        className="@max-3xl/cal:hidden"
      />
      <main
        className={cn(
          'flex flex-1 flex-col overflow-hidden rounded-lg border border-border/60 bg-background-app shadow-md shadow-black/30 transition-opacity duration-150',
          isPending && 'opacity-80',
        )}
      >
        <WeekToolbar
          visibleDate={visibleDate}
          onChangeWeek={handleDateSelect}
          onCreate={handleCreate}
          canCreate={manager !== null}
        />
        <WeekGrid manager={manager} schedule={schedule} visibleDate={visibleDate} />
      </main>
      <WindowEditorDialog />
    </div>
  )
}
